import { Datepicker } from 'components/form/Datepicker';
import { ConfirmationModal } from 'components/modals/ConfirmationModal';
import { AlertMessage } from 'components/notification/AlertMessage';
import { Toaster } from 'components/notification/Toaster';
import useAccountControlActions from 'dataflow/billingPortal/actions/useAccountControl.actions';
import { isArrayNotEmpty, isDateValid, isNullUndefined, isStringEmpty } from 'functions';
import { useCustomSelector } from 'hooks';
import React, { useEffect, useState } from 'react';
import useOutletsActions from 'dataflow/billingPortal/actions/useOutlet.actions';

export default function useModals(selectedOutlets) {
    const [isVisible_updatePaymentDueDate, setIsVisible_updatePaymentDueDate] = useState();
    const [newPaymentDueDate, setNewPaymentDueDate] = useState();

    const [isVisible_resetPaymentDueDate, setIsVisible_resetPaymentDueDate] = useState();

    const [isVisible_setSuspendDate, setIsVisible_setSuspendDate] = useState();
    const [suspendDate, setSuspendDate] = useState();

    const [isModalResetRequired, setIsModalResetRequired] = useState();

    // Reset
    useEffect(() => {
        if (!isVisible_setSuspendDate) {
            setSuspendDate("");
        }
    }, [isVisible_setSuspendDate]);

    useEffect(() => {
        if (!isVisible_updatePaymentDueDate && !isVisible_resetPaymentDueDate) {
            setNewPaymentDueDate("");
        }
    }, [isVisible_updatePaymentDueDate, isVisible_resetPaymentDueDate]);

    const {
        updateSuspendDate
    } = useOutletsActions();

    const {
        updatePaymentDueDate,
        resetPaymentDueDate,
    } = useAccountControlActions();

    const {
        isUpdateSuccessful,
        setSuspendDate_requesting, setSuspendDate_isSuccessful
    } = useCustomSelector("outlet");

    const {
        updatePaymentDueDate_requesting, updatePaymentDueDate_isSuccessful,
        resetPaymentDueDate_requesting, resetPaymentDueDate_isSuccessful
    } = useCustomSelector("accountControl");

    useEffect(() => {
        if (isUpdateSuccessful || updatePaymentDueDate_isSuccessful || resetPaymentDueDate_isSuccessful) {
            setIsVisible_setSuspendDate(false);
            setIsVisible_updatePaymentDueDate(false);
            setIsVisible_resetPaymentDueDate(false);
        }
    }, [isUpdateSuccessful, updatePaymentDueDate_isSuccessful, resetPaymentDueDate_isSuccessful]);

    // Reset inputs
    useEffect(() => {
        setIsModalResetRequired(true);
    }, [isVisible_setSuspendDate, isVisible_updatePaymentDueDate, isVisible_resetPaymentDueDate]);

    useEffect(() => {
        setIsModalResetRequired(false);
    }, [isModalResetRequired]);

    let updatePaymentDueDateModal = (
        <ConfirmationModal
            description={"Updating selected COM(s) to new payment due date"}
            input={(
                <Datepicker
                    label={"Date"}
                    handleOnChange={setNewPaymentDueDate}

                    isResetRequired={isModalResetRequired}
                    isConversionRequired_ISO8601
                />
            )}

            cancelButtonText={"Cancel"}
            confirmButtonText={"Confirm"}
            isModalVisible={isVisible_updatePaymentDueDate}
            updateParentState={setIsVisible_updatePaymentDueDate}
            handleOnConfirm={() => {
                if (isArrayNotEmpty(selectedOutlets)) {
                    selectedOutlets.map((details) => {
                        if (details?.ComId) {
                            updatePaymentDueDate(details?.ComId, newPaymentDueDate, details?.ID);
                        }
                        else {
                            Toaster({
                                type: "error",
                                msg: "COM ID required"
                            })
                        }
                    })
                }
            }}
            isActionLoading={updatePaymentDueDate_requesting}
            isConfirmSuccessful={updatePaymentDueDate_isSuccessful}
        />
    );

    let resetPaymentDueDateModal = (
        <ConfirmationModal
            bodyHeader={(
                <AlertMessage
                    headerMsg={"WARNING"}
                    message={"BE CAREFUL, THE SELECTED COM(s) PAYMENT DATE WILL BE SET AS EMPTY"}
                    alertType={"error"}
                    isVisible
                />
            )}

            description={(
                <>
                    <div className={""}>
                        Are you sure you want to reset the selected payment dates?
                    </div>

                    <div style={{
                        maxHeight: "100px",
                        overflow: "auto"
                    }}>
                        {isArrayNotEmpty(selectedOutlets)? selectedOutlets.map((details, idx) => {
                            return (
                                <div key={idx}>{"COM " + details?.ComId}</div>
                            )
                        }):""}
                    </div>
                </>
            )}
            cancelButtonText={"Cancel"}
            confirmButtonText={"Confirm"}
            isModalVisible={isVisible_resetPaymentDueDate}
            updateParentState={setIsVisible_resetPaymentDueDate}
            handleOnConfirm={() => {
                if (isArrayNotEmpty(selectedOutlets)) {
                    selectedOutlets.map((details) => {
                        if (details?.ComId) {
                            resetPaymentDueDate(details?.ComId, details?.ID);
                        }
                        else {
                            Toaster({
                                type: "error",
                                msg: "COM ID required"
                            })
                        }
                    })
                }
            }}
            isActionLoading={resetPaymentDueDate_requesting}
            isConfirmSuccessful={resetPaymentDueDate_isSuccessful}
            isWarning
        />
    );

    let setSuspendDateModal = (
        <ConfirmationModal
            description={"Set selected COM(s) suspend date"}
            input={(
                <Datepicker
                    label={"Suspend Date"}
                    handleOnChange={setSuspendDate}

                    isResetRequired={isModalResetRequired}
                    isConversionRequired_ISO8601
                    minDate={new Date(new Date().setDate(new Date().getDate() + 1))} // date after the current date
                />
            )}

            cancelButtonText={"Cancel"}
            confirmButtonText={"Confirm"}
            isModalVisible={isVisible_setSuspendDate}
            updateParentState={setIsVisible_setSuspendDate}
            handleOnConfirm={() => {
                // only com with suspend date can set to null
                // if suspend date null check if current value has suspend date
                if(!isDateValid(suspendDate)) {
                    for(let i = 0; i < selectedOutlets.length; i++) {
                        let suspendDate = selectedOutlets[i]?.SuspendDate
                        if(isNullUndefined(suspendDate) || isStringEmpty(suspendDate)) {
                            Toaster({
                                type: "error",
                                msg: "Suspend Date cannot set to null"
                            })
                            return;
                        }
                    }
                }

                if (isArrayNotEmpty(selectedOutlets)) {
                    selectedOutlets.map((details) => {
                        let comId = details?.ComId;

                        if (comId && details?.ID) {
                            updateSuspendDate(comId, details?.ID, suspendDate);
                        }
                        else {
                            Toaster({
                                type: "error",
                                msg: "ID required"
                            })
                        }
                    })
                }
            }}
            isActionLoading={setSuspendDate_requesting}
            isConfirmSuccessful={setSuspendDate_isSuccessful}
        />
    );

    return {
        updatePaymentDueDateModal,
        resetPaymentDueDateModal,
        setSuspendDateModal,
        setIsVisible_setSuspendDate,
        setIsVisible_updatePaymentDueDate,
        setIsVisible_resetPaymentDueDate
    }
}